<script setup>
import { computed, nextTick, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@chatwoot/pico-search';
import { messageStamp } from 'shared/helpers/timeHelper';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import AddAutomationRule from './AddAutomationRule.vue';
import EditAutomationRule from './EditAutomationRule.vue';
import { AUTOMATION_RULE_EVENTS } from './constants';
import { DEAL_AUTOMATION_RULE_EVENTS } from './dealAutomationBridge';
import ExecutionLogDrawer from 'dashboard/components/automation/ExecutionLogDrawer.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const addDialogRef = ref(null);
const editDialogRef = ref(null);
const showDeleteConfirmationPopup = ref(false);
const selectedRule = ref(null);
const loadingById = ref({});
const confirmDialog = ref(null);
const searchQuery = ref('');
const toggleModalTitle = ref(t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION')
);

const executionDrawerRule = ref(null);
const executionDrawerRef = ref(null);

const accountId = computed(() => getters.getCurrentAccountId.value);
const automationRecords = computed(
  () => store.getters['automations/getAutomations'] || []
);
const dealAutomationRecords = computed(
  () => store.getters['dealAutomationRules/getRules'] || []
);
const automationFlags = computed(
  () => store.getters['automations/getUIFlags'] || {}
);
const dealFlags = computed(
  () => store.getters['dealAutomationRules/getUIFlags'] || {}
);

const uiFlags = computed(() => ({
  isFetching:
    !!automationFlags.value.isFetching || !!dealFlags.value.isFetching,
}));

const normalizeTimestampToMs = value => {
  if (value === null || value === undefined || value === '') return 0;

  const num = Number(value);
  if (!Number.isNaN(num) && num > 0) {
    if (num > 1e15) return Math.floor(num / 1000); // microseconds
    if (num > 1e12) return Math.floor(num); // milliseconds
    if (num > 1e9) return Math.floor(num * 1000); // seconds
  }

  const parsed = Date.parse(String(value));
  if (!Number.isNaN(parsed)) return parsed;

  return 0;
};

const allRecords = computed(() => {
  const chatwoot = automationRecords.value
    .filter(rule => rule && rule.id)
    .map(rule => ({
      ...rule,
      source: 'automation',
      createdAtMs: normalizeTimestampToMs(rule.created_on || rule.created_at),
    }));
  const deals = dealAutomationRecords.value
    .filter(rule => rule && rule.id)
    .map(rule => ({
      ...rule,
      source: 'deal',
      createdAtMs: normalizeTimestampToMs(rule.created_at || rule.created_on),
    }));
  return [...chatwoot, ...deals].sort((a, b) => b.createdAtMs - a.createdAtMs);
});

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return allRecords.value;
  return picoSearch(allRecords.value, query, ['name', 'description']);
});

const fetchAll = () =>
  Promise.allSettled([
    store.dispatch('automations/get'),
    store.dispatch('dealAutomationRules/get', { accountId: accountId.value }),
  ]);

const LEGACY_DEAL_EVENT_LABELS = {
  deal_stage_changed: 'Estagio alterado',
  deal_won: 'Negocio ganho',
  deal_lost: 'Negocio perdido',
  deal_assignee_changed: 'Responsavel alterado',
};

const humanizeToken = token =>
  String(token || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, ch => ch.toUpperCase());

const eventLabel = eventName => {
  if (!eventName) return '-';
  const tryI18n = key => {
    const translated = t(`AUTOMATION.EVENTS.${key}`);
    return translated !== `AUTOMATION.EVENTS.${key}` ? translated : null;
  };
  const direct = tryI18n(eventName);
  if (direct) return direct;
  const event =
    AUTOMATION_RULE_EVENTS.find(item => item.key === eventName) ||
    DEAL_AUTOMATION_RULE_EVENTS.find(item => item.key === eventName);
  if (!event)
    return LEGACY_DEAL_EVENT_LABELS[eventName] || humanizeToken(eventName);
  if (event.value) {
    const fromValue = tryI18n(event.value);
    if (fromValue) return fromValue;
  }
  return event.label || event.value || event.key;
};

const readableDate = record => {
  const unixSeconds = Math.floor((record.createdAtMs || 0) / 1000);
  return unixSeconds ? messageStamp(unixSeconds, 'LLL d, yyyy') : '-';
};

const openAddPopup = () => addDialogRef.value?.open();
const hideAddPopup = async (shouldRefresh = false) => {
  addDialogRef.value?.close();
  if (shouldRefresh) await fetchAll();
};

const openEditPopup = rule => {
  if (!rule?.id) return;
  selectedRule.value = { ...rule };
  editDialogRef.value?.open();
};
const hideEditPopup = async (shouldRefresh = false) => {
  editDialogRef.value?.close();
  selectedRule.value = null;
  if (shouldRefresh) await fetchAll();
};

const openDeletePopup = rule => {
  if (!rule?.id) return;
  selectedRule.value = { ...rule };
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteMessage = computed(() => ` ${selectedRule.value?.name || ''}?`);

const setLoading = (id, value) => {
  loadingById.value = { ...loadingById.value, [id]: value };
};

const saveAutomation = async (payload, mode) => {
  try {
    if (mode === 'edit' && selectedRule.value?.source === 'deal') {
      await store.dispatch('dealAutomationRules/update', {
        accountId: accountId.value,
        id: selectedRule.value.id,
        rule: payload,
      });
      useAlert(t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE'));
    } else if (mode === 'edit') {
      await store.dispatch('automations/update', payload);
      useAlert(t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE'));
    } else if (
      payload?.event_name &&
      DEAL_AUTOMATION_RULE_EVENTS.some(e => e.key === payload.event_name)
    ) {
      await store.dispatch('dealAutomationRules/create', {
        accountId: accountId.value,
        rule: payload,
      });
      useAlert(t('AUTOMATION.ADD.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('automations/create', payload);
      useAlert(t('AUTOMATION.ADD.API.SUCCESS_MESSAGE'));
    }
    await hideAddPopup(false);
    await hideEditPopup(false);
  } catch (error) {
    useAlert(error?.message || t('AUTOMATION.ADD.API.ERROR_MESSAGE'));
  }
};

const toggleRule = async rule => {
  const id = rule.id;
  setLoading(id, true);
  try {
    if (rule.active) {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION',
        {
          automationName: rule.name,
        }
      );
    } else {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION',
        {
          automationName: rule.name,
        }
      );
    }

    const ok = await confirmDialog.value?.showConfirmation?.();
    if (!ok) return;

    if (rule.source === 'deal') {
      await store.dispatch('dealAutomationRules/update', {
        accountId: accountId.value,
        id,
        rule: { active: !rule.active },
      });
    } else {
      await store.dispatch('automations/update', { id, active: !rule.active });
    }
    useAlert(
      rule.active
        ? t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
        : t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL')
    );
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  } finally {
    setLoading(id, false);
  }
};

const confirmDeletion = async () => {
  const rule = selectedRule.value;
  const id = rule?.id;
  closeDeletePopup();
  if (!id) return;
  setLoading(id, true);
  try {
    if (rule.source === 'deal') {
      await store.dispatch('dealAutomationRules/delete', {
        accountId: accountId.value,
        id,
      });
    } else {
      await store.dispatch('automations/delete', id);
    }
    await fetchAll();
    useAlert(t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
  } catch (_error) {
    useAlert(t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
  } finally {
    setLoading(id, false);
  }
};

const cloneRule = async rule => {
  const id = rule.id;
  setLoading(id, true);
  try {
    if (rule.source === 'deal') {
      await store.dispatch('dealAutomationRules/clone', {
        accountId: accountId.value,
        id,
      });
    } else {
      await store.dispatch('automations/clone', id);
    }
    await fetchAll();
    useAlert(t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
  } catch (_error) {
    useAlert(t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
  } finally {
    setLoading(id, false);
  }
};

const openExecutionLog = rule => {
  executionDrawerRule.value = rule;
  nextTick(() => {
    executionDrawerRef.value?.open();
  });
};

onBeforeMount(() => {
  fetchAll();
});

const isAutomationLimitReached = computed(() => {
  const isAtLimit = store.getters['subscriptionLimits/isAtLimit'];
  return isAtLimit ? isAtLimit('automations') : false;
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION.LOADING')"
    :no-records-found="!allRecords.length"
    :no-records-message="$t('AUTOMATION.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AUTOMATION.HEADER')"
        :description="$t('AUTOMATION.DESCRIPTION')"
        :search-placeholder="$t('AUTOMATION.SEARCH_PLACEHOLDER')"
      >
        <template v-if="allRecords?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AUTOMATION.COUNT', { n: allRecords.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('AUTOMATION.HEADER_BTN_TXT')"
            :disabled="isAutomationLimitReached"
            :title="
              isAutomationLimitReached
                ? 'Limite de automações do plano atingido. Faça upgrade para adicionar mais.'
                : ''
            "
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <tr>
            <th
              class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
            >
              {{ $t('AUTOMATION.LIST.TABLE_HEADER.NAME') }}
            </th>
            <th
              class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
            >
              {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
            </th>
            <th
              class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
            >
              {{ $t('AUTOMATION.LIST.TABLE_HEADER.ACTIVE') }}
            </th>
            <th
              class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
            >
              {{ $t('AUTOMATION.LIST.TABLE_HEADER.CREATED_ON') }}
            </th>
            <th class="py-4 font-semibold text-right text-n-slate-11">
              {{ $t('AGENT_MGMT.LIST.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-12">
          <tr v-if="!filteredRecords.length && searchQuery">
            <td
              colspan="5"
              class="py-20 text-center text-body-main !text-base text-n-slate-11"
            >
              {{ $t('AUTOMATION.NO_RESULTS') }}
            </td>
          </tr>
          <tr v-for="rule in filteredRecords" :key="`${rule.source}-${rule.id}`">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="font-medium break-words text-n-slate-12">
                {{ rule.name }}
              </div>
              <div class="text-xs text-n-slate-10">{{ rule.description }}</div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ eventLabel(rule.event_name) }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <ToggleSwitch
                :model-value="rule.active"
                @update:model-value="toggleRule(rule)"
              />
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">{{ readableDate(rule) }}</td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="'Ver execuções'"
                  icon="i-lucide-history"
                  slate
                  xs
                  faded
                  @click="openExecutionLog(rule)"
                />
                <Button
                  v-tooltip.top="$t('AUTOMATION.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loadingById[rule.id]"
                  @click="openEditPopup(rule)"
                />
                <Button
                  v-tooltip.top="$t('AUTOMATION.CLONE.TOOLTIP')"
                  icon="i-lucide-copy-plus"
                  xs
                  faded
                  :is-loading="loadingById[rule.id]"
                  @click="cloneRule(rule)"
                />
                <Button
                  v-tooltip.top="$t('AUTOMATION.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loadingById[rule.id]"
                  @click="openDeletePopup(rule)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <AddAutomationRule
      ref="addDialogRef"
      @save-automation="saveAutomation"
    />

    <EditAutomationRule
      ref="editDialogRef"
      :selected-response="selectedRule || {}"
      @save-automation="saveAutomation"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AUTOMATION.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('AUTOMATION.DELETE.CONFIRM.YES')"
      :reject-text="$t('AUTOMATION.DELETE.CONFIRM.NO')"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />
  </SettingsLayout>

  <ExecutionLogDrawer
    v-if="executionDrawerRule"
    ref="executionDrawerRef"
    :rule-id="executionDrawerRule.id"
    :rule-type="executionDrawerRule.source === 'deal' ? 'DealAutomationRule' : 'AutomationRule'"
    :rule-name="executionDrawerRule.name"
  />
</template>
